import { useState } from "react";
import { Link } from "react-router-dom";
import {
  HelpCircle,
  Wrench,
  Building2,
  Users,
  Truck,
  Package,
  ChevronRight,
  ChevronDown,
  Search,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Clock,
  DollarSign,
  Camera,
  FileText,
  Send,
  Shield,
  UserPlus,
  ClipboardList
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SiteHeader from "@/components/SiteHeader";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";

// Help categories and content
const helpCategories = [
  {
    id: "quickbid",
    title: "QuickBid Exchange",
    subtitle: "Bypass the 3-quote bottleneck",
    icon: Shield,
    color: "bg-emerald-100 text-emerald-600",
    articles: [
      {
        id: "what-is-quickbid",
        title: "What is the QuickBid Exchange?",
        content: `
## QuickBid Exchange - Your Digital Safety Valve

AskIsaac's QuickBid Exchange expands your supplier and contractor network with competitive bidding and real-time tracking.

### The Value We Deliver
- Expand your vendor options with vetted suppliers
- Dispatch credentialed contractors in minutes
- Automated 3+ quote process for compliance
- Full visibility with GPS tracking

### How QuickBid Works
1. **Instant Matching** - We instantly identify pre-vetted, credentialed local contractors and suppliers
2. **Healthcare-Ready** - All partners verified with proper COI and licenses
3. **Competitive Pricing** - Multiple quotes in hours, not weeks
4. **Compliance Built-In** - AAMI ST108 and Joint Commission ready

### Benefits
- Hours instead of weeks for sourcing
- Pre-vetted local talent ready to respond
- Competitive pricing through real bidding
- Full accountability on every job

*"AskIsaac brings all the players together under one hood — so you can focus on patient care."*
        `
      },
      {
        id: "gpo-backup",
        title: "Expanding Your Network with AskIsaac",
        content: `
## Expand Your Healthcare Network

We're not replacing your existing relationships — we're adding depth to your bench with a vetted network of suppliers and contractors.

### When to Use AskIsaac
- Need additional supplier options
- Specialized contractor required
- Time-sensitive sourcing needed
- Capital project coordination
- Compliance documentation required

### How It Works
1. Submit your request through AskIsaac
2. We broadcast to 50+ verified suppliers
3. Receive competitive quotes within hours
4. Select best option and get delivery

### Cost Rectification
Many vendors over-spec systems where not needed:
- 510K equipment where basic solutions suffice
- Over-sized HVAC for "compliance theater"
- Hardware-heavy vs software-backed validation

**Result**: We've saved hospitals $1M+ on single-department water systems.
        `
      }
    ]
  },
  {
    id: "hospital",
    title: "Hospital Users",
    subtitle: "Submit requests & track status",
    icon: Building2,
    color: "bg-blue-100 text-blue-600",
    articles: [
      {
        id: "submit-maintenance",
        title: "How to Submit a Maintenance Request",
        content: `
## Submitting a Maintenance Request

1. **Navigate to Maintenance Portal**
   - Click "Maintenance" in the main navigation or go to /maintenance

2. **Select Issue Type**
   - Choose from categories like Water Leak, Equipment Malfunction, HVAC Issues, etc.

3. **Provide Details**
   - Enter your facility name and address
   - Add a detailed description of the issue
   - Select the priority level (Normal, High, Emergency)
   - Upload photos if available (recommended)

4. **Submit Contact Information**
   - Provide your name, email, and phone number
   - This ensures we can reach you for updates

5. **Track Your Request**
   - After submission, you'll receive a confirmation with a request ID
   - Use this ID to track status or contact support

### Tips for Faster Service
- Be as specific as possible in your description
- Include photos of the issue
- Provide accurate location details (building, floor, room)
- Select appropriate priority level
        `
      },
      {
        id: "track-request",
        title: "Tracking Your Request Status",
        content: `
## Understanding Request Status

Your maintenance request goes through these stages:

| Stage | Description |
|-------|-------------|
| **Intake** | Request received, pending review |
| **Triage** | Being assessed, work order created |
| **Dispatch** | Assigned to contractor |
| **In Progress** | Technician actively working |
| **Completion** | Work finished, awaiting sign-off |
| **Closed** | Complete and archived |

### How to Check Status
- Contact your Sterile Mate representative
- Reference your request ID from the confirmation email
- Admin users can view all requests in the dashboard
        `
      },
      {
        id: "backorders",
        title: "Submitting Backorder Requests",
        content: `
## Backorder Requests

For items that are out of stock or hard to find:

1. Go to **Hospital Dashboard** → **Backorders**
2. Fill in the item details:
   - Product name and description
   - Quantity needed
   - Urgency level
   - Preferred vendor (if any)
3. Submit the request
4. Our procurement team will source the item and provide a quote

### What Happens Next
- A procurement specialist reviews your request
- We search our supplier network for availability
- You'll receive a quote with pricing and lead time
- Approve the quote to proceed with the order
        `
      }
    ]
  },
  {
    id: "contractor",
    title: "Contractors",
    subtitle: "Manage jobs & team",
    icon: Truck,
    color: "bg-emerald-100 text-emerald-600",
    articles: [
      {
        id: "job-workflow",
        title: "Job Workflow: From Dispatch to Completion",
        content: `
## Complete Job Workflow

### 1. Receiving a Dispatch
- You'll receive a notification when assigned a job
- Click "View & Accept" in your dashboard or use the dispatch link

### 2. Job Status Updates
Update your status as you progress:

| Status | When to Update |
|--------|----------------|
| **Acknowledged** | When you accept the job |
| **En Route** | When leaving for the site |
| **On Site** | Upon arrival at the facility |
| **Completed** | When work is finished |

### 3. Completion Requirements
Before submitting an invoice:
- ✅ Upload completion photos (proof of work)
- ✅ Get hospital staff signature
- ✅ Fill out completion notes

### 4. Invoice Submission
- Enter labor hours and any parts used
- Add description of work performed
- Submit for approval

### Warranty Jobs
Jobs marked "WARRANTY" have restrictions:
- No labor charges allowed without override code
- Parts may be covered - check with admin
- Contact your admin for override if needed
        `
      },
      {
        id: "team-management",
        title: "Managing Your Team",
        content: `
## Team Management

Add team members to give them access to jobs assigned to your company.

### Adding a Team Member
1. Go to **Contractor Dashboard** → **Team** tab
2. Click **Add Member**
3. Enter their details:
   - Name
   - Email
   - Phone (optional)
   - Role

### Team Roles

| Role | Permissions |
|------|-------------|
| **View Only** | Can see jobs, cannot update status |
| **Field Tech** | Can update job status, upload photos |
| **Manager** | Full access including team management |

### Best Practices
- Add all your field technicians as "Field Tech"
- Use "Manager" role sparingly for supervisors
- Remove team members promptly when they leave
        `
      },
      {
        id: "completion-certificate",
        title: "Completion Certificate & Sign-off",
        content: `
## Completion Certificate

The completion certificate is required to close a job and submit an invoice.

### What You Need
1. **Completion Photos**
   - Take clear photos of the completed work
   - Include before/after if possible
   - Minimum 1 photo required

2. **Hospital Sign-off**
   - Have a hospital staff member sign on your device
   - They must provide their name and signature
   - This confirms the work was completed satisfactorily

3. **Completion Notes**
   - Describe the work performed
   - List any parts replaced
   - Note any follow-up required

### Digital Signature
- Use finger or stylus on the signature pad
- Clear and redo if needed
- Both name and signature are required
        `
      }
    ]
  },
  {
    id: "admin",
    title: "Administrators",
    subtitle: "Full platform management",
    icon: Shield,
    color: "bg-purple-100 text-purple-600",
    articles: [
      {
        id: "pipeline-overview",
        title: "Maintenance Pipeline Overview",
        content: `
## Maintenance Pipeline

The pipeline provides a Kanban-style view of all maintenance requests.

### Pipeline Stages

| Stage | Description | Actions |
|-------|-------------|---------|
| **Intake** | New requests | Create Work Order |
| **Triage** | Work order created | Dispatch to contractor |
| **Dispatch** | Assigned to contractor | Monitor progress |
| **In Progress** | Contractor working | Check for updates |
| **Completion** | Work done | Review & close |
| **Closed** | Archived | View history |

### Creating a Work Order
1. Click on a request in **Intake**
2. Click **Create Work Order**
3. Fill in:
   - Scope of work
   - Estimated hours & cost
   - Priority level
   - Due date
   - Special instructions

### Dispatching to Contractor
1. Find the work order in **Triage**
2. Click **Dispatch**
3. Select contractor from dropdown
4. Add expected arrival time
5. Include any dispatch notes
        `
      },
      {
        id: "view-as",
        title: "Using View As Mode",
        content: `
## View As Mode

Test the experience of different user types without logging out.

### How to Use
1. In the Admin Dashboard header, look for the eye icon
2. Select **View as Hospital**, **View as Contractor**, or **View as Supplier**
3. You'll see the interface as that user type
4. Click **Exit View As** to return to admin mode

### Use Cases
- Test user experience before launch
- Debug issues reported by users
- Verify access controls are working
- Demo features to stakeholders

### Limitations
- View As is read-only for most actions
- Some admin functions remain available
- Session returns to admin on logout
        `
      },
      {
        id: "security-audit",
        title: "Security Dashboard & Audit Logs",
        content: `
## Security Features

### Security Dashboard
Access via **Admin Dashboard** → **Security** tab

**What You'll See:**
- Active user sessions
- Login attempts (success/failure)
- Security score and recommendations
- Recent security events

### Audit Logging
All important actions are logged:
- User logins/logouts
- Work order creation and updates
- Status changes
- User account changes

### Best Practices
- Review failed login attempts weekly
- Monitor for unusual session activity
- Close inactive sessions regularly
- Update passwords quarterly
        `
      }
    ]
  },
  {
    id: "supplier",
    title: "Suppliers",
    subtitle: "Quotes & orders",
    icon: Package,
    color: "bg-amber-100 text-amber-600",
    articles: [
      {
        id: "rfq-response",
        title: "Responding to RFQs",
        content: `
## Responding to Requests for Quote

### Accessing RFQs
1. Log into the Supplier Portal
2. View **Pending RFQs** on your dashboard
3. Click on an RFQ to see details

### Submitting a Quote
1. Review the requested items and quantities
2. Enter your pricing:
   - Unit price per item
   - Any volume discounts
   - Shipping costs
3. Add lead time estimate
4. Include any terms or conditions
5. Submit the quote

### Quote Status
- **Pending** - Awaiting your response
- **Submitted** - Quote sent, awaiting decision
- **Accepted** - You won the business!
- **Declined** - Not selected this time

### Tips for Winning Quotes
- Respond quickly - speed matters
- Be competitive on pricing
- Offer volume discounts when possible
- Provide accurate lead times
        `
      }
    ]
  }
];

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const filteredCategories = helpCategories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.articles.length > 0);

  if (selectedArticle) {
    const category = helpCategories.find(c => c.articles.some(a => a.id === selectedArticle.id));
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Button variant="ghost" onClick={() => setSelectedArticle(null)} className="mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Help
            </Button>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${category?.color} flex items-center justify-center`}>
                {category?.icon && <category.icon className="w-5 h-5" />}
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">{selectedArticle.title}</h1>
                <p className="text-sm text-slate-500">{category?.title}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <article className="bg-white rounded-xl border border-slate-200 p-8 prose prose-slate max-w-none">
            <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ 
              __html: selectedArticle.content
                .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-slate-900 mt-6 mb-4">$1</h2>')
                .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold text-slate-800 mt-4 mb-2">$1</h3>')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\|(.*)\|/g, (match) => {
                  if (match.includes('---')) return '<tr class="border-b border-slate-200"></tr>';
                  const cells = match.split('|').filter(Boolean);
                  return `<tr>${cells.map(cell => `<td class="px-4 py-2 border-b border-slate-100">${cell.trim()}</td>`).join('')}</tr>`;
                })
                .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
                .replace(/^\d+\. (.*$)/gm, '<li class="ml-4 list-decimal">$1</li>')
                .replace(/✅/g, '<span class="text-green-500">✅</span>')
            }} />
          </article>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img src={LOGO_URL} alt="Ask Isaac" className="h-10" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <HelpCircle className="w-6 h-6 text-blue-600" />
                  Help Center
                </h1>
                <p className="text-slate-500 text-sm">User guides and documentation</p>
              </div>
            </div>
            <Link to="/">
              <Button variant="outline">Back to Portal</Button>
            </Link>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search help articles..."
              className="pl-10"
            />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Quick Links */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {helpCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
              className={`p-4 rounded-xl border border-slate-200 text-left hover:border-blue-300 transition-all ${
                expandedCategory === category.id ? 'bg-blue-50 border-blue-300' : 'bg-white'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center mb-3`}>
                <category.icon className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-slate-900">{category.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{category.subtitle}</p>
            </button>
          ))}
        </div>

        {/* Articles */}
        <div className="space-y-6">
          {(searchQuery ? filteredCategories : helpCategories).map((category) => (
            <div key={category.id} className={`${
              expandedCategory && expandedCategory !== category.id ? 'hidden' : ''
            }`}>
              <button
                onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center`}>
                    <category.icon className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-slate-900">{category.title}</h3>
                    <p className="text-sm text-slate-500">{category.articles.length} articles</p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${
                  expandedCategory === category.id ? 'rotate-180' : ''
                }`} />
              </button>

              {(expandedCategory === category.id || searchQuery) && (
                <div className="mt-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {category.articles.map((article, idx) => (
                    <button
                      key={article.id}
                      onClick={() => setSelectedArticle(article)}
                      className={`w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left ${
                        idx > 0 ? 'border-t border-slate-100' : ''
                      }`}
                    >
                      <span className="font-medium text-slate-700">{article.title}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Support */}
        <div className="mt-12 bg-blue-50 rounded-xl p-6 border border-blue-100">
          <h3 className="font-semibold text-blue-900 mb-2">Still need help?</h3>
          <p className="text-blue-700 text-sm mb-4">
            Our support team is available Monday-Friday, 8am-6pm EST.
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2 text-blue-800">
              <FileText className="w-4 h-4" />
              support@sterilemate.com
            </div>
            <div className="flex items-center gap-2 text-blue-800">
              <Clock className="w-4 h-4" />
              800.988.3892
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
